/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isAlmostSameValueF64 from '@stdlib/number/float64/base/assert/is-almost-same-value';
import isAlmostSameValueF32 from '@stdlib/number/float32/base/assert/is-almost-same-value';
import reim from './../../../../float64/reim';
import reimf from './../../../../float32/reim';


// MAIN //

/**
* Tests whether two complex numbers are approximately the same value within a specified number of ULPs (units in the last place).
*
* ## Notes
*
* -   The function implements the [SameValue Algorithm][ecma-262-same-value-algorithm], as specified in ECMAScript 5.
* -   In contrast to the strict equality operator `===`, `-0` and `+0` are distinguishable and `NaNs` are the same.
*
* [ecma-262-same-value-algorithm]: http://ecma-international.org/ecma-262/5.1/#sec-9.12
*
* @param {ComplexLike} z1 - first complex number
* @param {ComplexLike} z2 - second complex number
* @param {number} maxULP - maximum allowed ULP difference
* @returns {boolean} boolean indicating whether two complex numbers are approximately the same value within a specified number of ULPs
*
* @example
* import EPS from '@stdlib/constants/float64/eps';
* import Complex128 from '@stdlib/complex/float64/ctor';
*
* var z1 = new Complex128( 1.0, 3.0 );
* var z2 = new Complex128( 1.0+EPS, 3.0 );
*
* var bool = isAlmostSameValue( z1, z2, 0 );
* // returns false
*
* bool = isAlmostSameValue( z1, z2, 1 );
* // returns true
*/
function isAlmostSameValue( z1, z2, maxULP ) {
	var parts1;
	var parts2;
	var nb1;
	var nb2;
	var f;

	// Complex-like values will not, in general, have these properties, but complex instances will, thus allowing us to distinguish between complex number data types without expensive constructor checks, including when given cross-realm complex number instances:
	nb1 = z1.BYTES_PER_ELEMENT;
	nb2 = z2.BYTES_PER_ELEMENT;

	// Case: isAlmostSameValue( complex64, complex64 )
	if ( nb1 === 4 && nb2 === 4 ) {
		f = isAlmostSameValueF32;
		parts1 = reimf( z1 );
		parts2 = reimf( z2 );
	}
	// Case: isAlmostSameValue( complexLike, complexLike )
	else {
		f = isAlmostSameValueF64;
		parts1 = reim( z1 );
		parts2 = reim( z2 );
	}
	return (
		f( parts1[ 0 ], parts2[ 0 ], maxULP ) &&
		f( parts1[ 1 ], parts2[ 1 ], maxULP )
	);
}


// EXPORTS //

export default isAlmostSameValue;
